/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaEnvelope,
  FaFacebookF,
  FaGithub,
  FaInstagram,
  FaLinkedinIn,
  FaSnapchat,
  FaThreads,
  FaXTwitter,
} from "react-icons/fa6";
import {
  collection,
  doc,
  getDocs,
  increment,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { HashLoader } from "react-spinners";
import { db } from "./backend/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_IMAGE_URL } from "./utils/constants";
const UserLink = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const slug = router.pathname.replace("/", "");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  let userUid = null;
  const [count, setCount] = useState(0);
  const [uid, setUid] = useState("");

  // SEO Helper Functions
  const generatePageTitle = () => {
    if (!data || Object.keys(data).length === 0) {
      return `@${slug} - Link Forest | Bio Link Page`;
    }
    return data.name
      ? `${data.name} (@${data.username}) - Link Forest | Bio Link Page`
      : `@${data.username} - Link Forest | Bio Link Page`;
  };

  const generatePageDescription = () => {
    if (!data || Object.keys(data).length === 0) {
      return `Create your bio link page like @${slug}. Link Forest - Connect all your links in one place. Free bio link tool for creators, influencers, and businesses.`;
    }

    const baseDescription = data.description
      ? `${data.description} | `
      : `${data.name || `@${data.username}`}'s links | `;

    const linksCount = data.websites ? Object.keys(data.websites).length : 0;
    const socialLinksCount = data.socialLinks ? Object.keys(data.socialLinks).filter(key => data.socialLinks[key]).length : 0;

    return `${baseDescription}Find all of ${data.name || `@${data.username}`}'s important links in one place. ${linksCount} links and ${socialLinksCount} social profiles on Link Forest.`;
  };

  const generateKeywords = () => {
    const baseKeywords = [
      "bio link",
      "link in bio",
      "social media links",
      "link tree",
      "link forest",
      "creator links",
      "influencer bio",
      slug.toLowerCase()
    ];

    if (data && data.name) {
      baseKeywords.push(data.name.toLowerCase());
    }

    if (data && data.socialLinks) {
      Object.keys(data.socialLinks).forEach(platform => {
        if (data.socialLinks[platform]) {
          baseKeywords.push(`${platform} profile`);
        }
      });
    }

    return baseKeywords.join(", ");
  };

  const getCurrentUrl = () => {
    return `${window.location.origin}/${slug}`;
  };

  const generateStructuredData = () => {
    if (!data || Object.keys(data).length === 0) return null;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": data.name || `@${data.username}`,
      "alternateName": `@${data.username}`,
      "description": data.description || `${data.name || `@${data.username}`}'s bio link page`,
      "url": getCurrentUrl(),
      "image": data.image || DEFAULT_IMAGE_URL,
      "sameAs": []
    };

    // Add social media profiles
    if (data.socialLinks) {
      Object.entries(data.socialLinks).forEach(([platform, url]) => {
        if (url) {
          structuredData.sameAs.push(url);
        }
      });
    }

    // Add websites
    if (data.websites) {
      Object.values(data.websites).forEach(website => {
        if (website.link) {
          structuredData.sameAs.push(website.link);
        }
      });
    }

    return JSON.stringify(structuredData);
  };
  useEffect(() => {
    getDataFromServer();
  }, [slug]);

  const getDataFromServer = async () => {
    setLoading(true);
    const q = query(
      collection(db, "Link Forests"),
      where("username", "==", slug)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      querySnapshot.forEach((doc) => {
        userUid = doc.id;
        setUid(doc.id);
        const userData = doc.data();
        setData(userData);
      });

      count === 0 && updateAnalytics(userUid);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (data?.gaMeasurementId) {
      const script1 = document.createElement("script");
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${data.gaMeasurementId}`;
      document.head.appendChild(script1);

      const script2 = document.createElement("script");
      script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${data.gaMeasurementId}');
          `;
      document.head.appendChild(script2);

      return () => {
        document.head.removeChild(script1);
        document.head.removeChild(script2);
        // Optional: clear dataLayer if needed, but removing scripts is the main request
        // window.dataLayer = []; 
      };
    }
  }, [data?.gaMeasurementId]);

  const updateAnalytics = async (uid) => {
    setCount((prev) => prev + 1);
    if (!uid) return;
    try {
      const currentDate = new Date();
      const day = String(currentDate.getDate());
      const month = String(currentDate.getMonth() + 1);
      const year = String(currentDate.getFullYear()).slice(-2);
      const encodedDate = `${day}-${month}-${year}`;
      const analyticsRef = doc(db, "Analytics", uid);
      await setDoc(
        analyticsRef,
        { [encodedDate]: increment(1) },
        { merge: true }
      );
      setCount((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating analytics:", error);
    }
  };

  const addLinkAnalyticsData = async (link, title) => {
    trackEvent("click", "Website Link", title || link);
    const currentDate = new Date();
    const day = String(currentDate.getDate());
    const month = String(currentDate.getMonth() + 1);
    const year = String(currentDate.getFullYear()).slice(-2);
    const encodedDate = `${day}-${month}-${year}`;
    const analyticsRef = doc(db, "Analytics", uid);
    await setDoc(
      analyticsRef,
      {
        links: {
          [link]: {
            [encodedDate]: increment(1),
          },
        },
      },
      { merge: true }
    );
  };

  const trackEvent = (action, category, label, value) => {
    if (window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  };

  const socialLinksAnalytics = async (social) => {
    trackEvent("click", "Social Link", social);
    const currentDate = new Date();
    const day = String(currentDate.getDate());
    const month = String(currentDate.getMonth() + 1);
    const year = String(currentDate.getFullYear()).slice(-2);
    const encodedDate = `${day}-${month}-${year}`;
    const analyticsRef = doc(db, "Analytics", uid);
    await setDoc(
      analyticsRef,
      {
        socialLinks: {
          [social]: {
            [encodedDate]: increment(1),
          },
        },
      },
      { merge: true }
    );
  };

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{generatePageTitle()}</title>
        <meta name="description" content={generatePageDescription()} />
        <meta name="keywords" content={generateKeywords()} />
        <meta name="author" content={data?.name || `@${data?.username || slug}`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="language" content="en" />

        {/* Canonical URL */}
        <link rel="canonical" href={getCurrentUrl()} />

        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={generatePageTitle()} />
        <meta property="og:description" content={generatePageDescription()} />
        <meta property="og:url" content={getCurrentUrl()} />
        <meta property="og:site_name" content="Link Forest" />
        <meta property="og:image" content={data?.image || DEFAULT_IMAGE_URL} />
        <meta property="og:image:alt" content={`${data?.name || `@${data?.username || slug}`}'s profile picture`} />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="400" />
        <meta property="og:locale" content="en_US" />
        {data?.name && <meta property="profile:first_name" content={data.name.split(' ')[0]} />}
        {data?.name && data.name.split(' ').length > 1 && <meta property="profile:last_name" content={data.name.split(' ').slice(1).join(' ')} />}
        <meta property="profile:username" content={data?.username || slug} />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={generatePageTitle()} />
        <meta name="twitter:description" content={generatePageDescription()} />
        <meta name="twitter:image" content={data?.image || DEFAULT_IMAGE_URL} />
        <meta name="twitter:image:alt" content={`${data?.name || `@${data?.username || slug}`}'s profile picture`} />
        <meta name="twitter:site" content="@linkforest" />
        <meta name="twitter:creator" content={`@${data?.username || slug}`} />

        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={data?.customTheme?.background ? `#${data.customTheme.background.toString(16).padStart(6, '0')}` : "#ffffff"} />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={data?.name || `@${data?.username || slug}`} />

        {/* Structured Data */}
        {generateStructuredData() && (
          <script type="application/ld+json">
            {generateStructuredData()}
          </script>
        )}

        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://res.cloudinary.com" />

        {/* Additional meta for better indexing */}
        <meta name="geo.region" content="Global" />
        <meta name="geo.placename" content="Worldwide" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="revisit-after" content="7 days" />
      </Helmet>

      <div
        className="flex justify-start items-center flex-col min-h-[100vh] pb-10"
        style={{
          backgroundColor: `${data?.customTheme?.background.toString(16)}`,
        }}
      >
        <svg
          onClick={() => navigate("/")}
          className="absolute top-5 left-5 cursor-pointer transition-all duration-300 ease-linear hover:ease-linear hover:scale-110 hover:duration-300 hover:transition-all"
          width="38"
          height="38"
          viewBox="0 0 500 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="500"
            height="500"
            rx="43.3054"
            fill={data?.customTheme?.textColor.toString(16)}
          ></rect>
          <path
            d="M217.087 351.889C209.764 338.018 191.21 326.302 191.21 215.04V213.616L203.363 221.71C213.021 228.134 220.939 236.848 226.412 247.073C231.885 257.3 234.743 268.723 234.73 280.322H256.491C256.506 265.143 252.766 250.194 245.603 236.812C238.44 223.428 228.078 212.025 215.44 203.617L199.946 193.281H216.593C233.067 193.312 249.092 198.654 262.289 208.513L282.603 223.744L295.659 206.337L275.345 191.105C258.374 178.434 237.772 171.567 216.593 171.521H203.363C217.575 157.644 236.627 149.84 256.491 149.76H278.251V128H256.491C241.873 128.054 227.452 131.368 214.277 137.699C201.101 144.031 189.504 153.221 180.329 164.601C171.154 153.221 159.557 144.031 146.382 137.699C133.207 131.368 118.786 128.054 104.169 128H82.4082V149.76H104.169C124.031 149.84 143.084 157.644 157.296 171.521H144.066C122.886 171.567 102.285 178.434 85.3132 191.105L65 206.337L78.0562 223.744L98.3693 208.513C111.567 198.654 127.592 193.312 144.066 193.281H160.712L145.23 203.617C132.589 212.023 122.225 223.426 115.061 236.81C107.896 250.194 104.154 265.141 104.169 280.322H125.929C125.916 268.723 128.773 257.3 134.246 247.073C139.719 236.848 147.638 228.134 157.296 221.71L169.449 213.626V215.04C169.449 312.353 181.357 347.04 191.682 371.358H196.549H248.322H295.659V351.889H217.087Z"
            fill={data?.customTheme?.background.toString(16)}
          ></path>
          <path
            d="M435.43 149.403V179.189H340.945V244.848H414.611V274.633H340.945V372H304.433V149.403H435.43Z"
            fill={data?.customTheme?.background.toString(16)}
          ></path>
        </svg>
        {!loading && Object.keys(data).length === 0 && (
          <div className="flex justify-center items-center flex-col h-[100vh]">
            <svg
              onClick={() => navigate("/")}
              className="absolute top-5 left-5 cursor-pointer transition-all duration-300 ease-linear hover:ease-linear hover:scale-110 hover:duration-300 hover:transition-all"
              width="38"
              height="38"
              viewBox="0 0 500 500"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="500" height="500" rx="43.3054" fill="#000"></rect>
              <path
                d="M217.087 351.889C209.764 338.018 191.21 326.302 191.21 215.04V213.616L203.363 221.71C213.021 228.134 220.939 236.848 226.412 247.073C231.885 257.3 234.743 268.723 234.73 280.322H256.491C256.506 265.143 252.766 250.194 245.603 236.812C238.44 223.428 228.078 212.025 215.44 203.617L199.946 193.281H216.593C233.067 193.312 249.092 198.654 262.289 208.513L282.603 223.744L295.659 206.337L275.345 191.105C258.374 178.434 237.772 171.567 216.593 171.521H203.363C217.575 157.644 236.627 149.84 256.491 149.76H278.251V128H256.491C241.873 128.054 227.452 131.368 214.277 137.699C201.101 144.031 189.504 153.221 180.329 164.601C171.154 153.221 159.557 144.031 146.382 137.699C133.207 131.368 118.786 128.054 104.169 128H82.4082V149.76H104.169C124.031 149.84 143.084 157.644 157.296 171.521H144.066C122.886 171.567 102.285 178.434 85.3132 191.105L65 206.337L78.0562 223.744L98.3693 208.513C111.567 198.654 127.592 193.312 144.066 193.281H160.712L145.23 203.617C132.589 212.023 122.225 223.426 115.061 236.81C107.896 250.194 104.154 265.141 104.169 280.322H125.929C125.916 268.723 128.773 257.3 134.246 247.073C139.719 236.848 147.638 228.134 157.296 221.71L169.449 213.626V215.04C169.449 312.353 181.357 347.04 191.682 371.358H196.549H248.322H295.659V351.889H217.087Z"
                fill="#fff"
              ></path>
              <path
                d="M435.43 149.403V179.189H340.945V244.848H414.611V274.633H340.945V372H304.433V149.403H435.43Z"
                fill="#fff"
              ></path>
            </svg>
            <p className="text-2xl font-bold font-Montserrat text-center">
              Claim <span className="text-emerald-500">@{slug}</span> Link Forest
              Now
            </p>
            <button
              className="bg-slate-950 px-4 py-2 rounded-md mt-4 text-slate-50 font-Montserrat font-medium text-center"
              onClick={() => navigate("/")}
            >
              Claim This Link Forest
            </button>
          </div>
        )}
        {!loading && Object.keys(data)?.length !== 0 && (
          <>
            <img
              key={data.image || 'default'}
              src={
                data.image
                  ? data.image
                  : DEFAULT_IMAGE_URL
              }
              alt=""
              className="rounded-full shadow-2xl object-cover mt-16 h-[100px] w-[100px]"
              onError={(e) => {
                e.target.src = DEFAULT_IMAGE_URL;
              }}
            />
            <p
              className="font-Montserrat text-2xl font-semibold mt-6 text-center w-[85%] md:w-[50%]"
              style={{
                color: `${data?.customTheme?.textColor.toString(16)}`,
              }}
            >
              {data.name ? data?.name : `@${data.username}`}
            </p>
            <p
              className="font-Montserrat text-base font-medium text-center mt-2 w-[85%] md:w-[50%]"
              style={{
                color: `${data?.customTheme?.textColor.toString(16)}`,
              }}
            >
              {data?.description}
            </p>
            <div className="flex justify-evenly items-center gap-4 mt-5">
              {data?.socialLinks?.facebook && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Facebook")}
                  href={data?.socialLinks?.facebook}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaFacebookF />
                </a>
              )}
              {data?.socialLinks?.twitter && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Twitter")}
                  href={data?.socialLinks?.twitter}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaXTwitter />
                </a>
              )}
              {data?.socialLinks?.instagram && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Instagram")}
                  href={data?.socialLinks?.instagram}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaInstagram />
                </a>
              )}
              {data?.socialLinks?.linkedin && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Linkedin")}
                  href={data?.socialLinks?.linkedin}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaLinkedinIn />
                </a>
              )}
              {data?.socialLinks?.github && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Github")}
                  href={data?.socialLinks?.github}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaGithub />
                </a>
              )}
              {data?.socialLinks?.threads && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Threads")}
                  href={data?.socialLinks?.threads}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaThreads />
                </a>
              )}
              {data?.socialLinks?.email && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Mail")}
                  href={`mailto:${data?.socialLinks?.email}`}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaEnvelope />
                </a>
              )}
              {data?.socialLinks?.snapchat && (
                <a
                  rel="noreferrer"
                  style={{
                    color: `${data?.customTheme?.textColor?.toString(16)}`,
                  }}
                  target="_blank"
                  onClick={() => socialLinksAnalytics("Snapchat")}
                  href={data?.socialLinks?.snapchat}
                  className="text-lg transition-all duration-300 ease-linear hover:ease-linear hover:scale-125 hover:duration-300 hover:transition-all"
                >
                  <FaSnapchat />
                </a>
              )}
            </div>
            <ul className="flex justify-center items-center flex-col gap-4 mt-6 pb-4 w-[85%] md:w-[30%] 2xl:w-[24%]">
              {data.websites &&
                Object.keys(data?.websites).map((website, index) => (
                  <li
                    style={{
                      border: `1.4px solid ${data?.themeType !== "custom" &&
                        data?.customTheme?.stroke?.toString(16)
                        }`,
                      color: `${data?.customTheme?.linkColor?.toString(16)}`,
                      backgroundColor: `${data?.customTheme?.linkBackground?.toString(
                        16
                      )}`,
                    }}
                    onClick={() => {
                      window.open(data?.websites[website]?.link);
                      addLinkAnalyticsData(data.websites[website].link, data.websites[website].title);
                    }}
                    key={index}
                    className="bg-slate-200 p-2 w-full text-center rounded-md font-Montserrat transition-all duration-300 ease-linear hover:ease-linear hover:scale-105 hover:duration-300 hover:transition-all cursor-pointer"
                  >
                    {data.websites[website].title}
                  </li>
                ))}
            </ul>
          </>
        )}
        {loading && (
          <div className="flex justify-center items-center h-[100vh]">
            <HashLoader color="#34d399" />
          </div>
        )}
      </div>
    </>
  );
};

export default UserLink;
